import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { toast } from 'react-toastify';
import { API_BASE_URL, BASE_URL,institute_id } from '../config/config';
/**
 * AI Mock Test Generator Page
 * Creates comprehensive mock tests for exam preparation
 */
const AIMockTestGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkFeatureAccess, logFeatureUsage } = useSubscription();
  
  const [formData, setFormData] = useState({
    examName: '',
    subjects: '',
    totalQuestions: 50,
    duration: 120,
    difficulty: 'medium'
  });
  const [generatedTest, setGeneratedTest] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const access = await checkFeatureAccess('mock_test_generator');
    if (!access.has_access) {
      toast.error(access.message || 'You need to upgrade to use this feature');
      navigate('/subscription/plans');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerate = async () => {
    if (!formData.examName.trim() || !formData.subjects.trim()) {
      toast.error('Please enter exam name and subjects');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/ai-subscription/features/mock-test/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Institute-ID': user?.institute_id || '',
        },
        body: JSON.stringify({
          exam_name: formData.examName,
          subjects: formData.subjects,
          num_questions: formData.totalQuestions,
          duration: formData.duration,
          difficulty: formData.difficulty,
          user_id: user?.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedTest(data.data);
        
        await logFeatureUsage('mock_test_generator', {
          exam: formData.examName.substring(0, 50),
          questions: formData.totalQuestions
        });
        
        toast.success('Mock test generated successfully!');
      } else {
        toast.error(data.message || 'Failed to generate mock test');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate mock test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = () => {
    if (!generatedTest) return;
    
    navigate('/ai-quiz', {
      state: {
        questions: generatedTest.questions,
        config: {
          examDetails: {
            title: generatedTest.exam_name || formData.examName,
            duration: formData.duration,
            totalMarks: generatedTest.questions.length,
            isMockTest: true
          }
        }
      }
    });
  };

  const handleReset = () => {
    setFormData({
      examName: '',
      subjects: '',
      totalQuestions: 50,
      duration: 120,
      difficulty: 'medium'
    });
    setGeneratedTest(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/ai/features')}
            className="text-purple-600 hover:text-purple-700 mb-4 flex items-center gap-2"
          >
            ← Back to AI Features
          </button>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            📋 AI Mock Test Generator
          </h1>
          <p className="text-gray-600">
            Create comprehensive mock tests for exam preparation with real exam patterns
          </p>
        </div>

        {!generatedTest ? (
          // Generation Form
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                  ⚙️ Test Configuration
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Exam Name *
                    </label>
                    <input
                      type="text"
                      name="examName"
                      value={formData.examName}
                      onChange={handleInputChange}
                      placeholder="e.g., JEE Mains Mock Test, NEET Practice Test"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subjects/Topics *
                    </label>
                    <textarea
                      name="subjects"
                      value={formData.subjects}
                      onChange={handleInputChange}
                      placeholder="e.g., Physics, Chemistry, Mathematics&#10;Or: World History, Geography, Civics"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[100px] resize-y"
                      disabled={loading}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Questions: {formData.totalQuestions}
                      </label>
                      <input
                        type="range"
                        name="totalQuestions"
                        min="20"
                        max="100"
                        step="10"
                        value={formData.totalQuestions}
                        onChange={handleInputChange}
                        className="w-full"
                        disabled={loading}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>20</span>
                        <span>100</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration: {formData.duration} minutes
                      </label>
                      <input
                        type="range"
                        name="duration"
                        min="30"
                        max="180"
                        step="15"
                        value={formData.duration}
                        onChange={handleInputChange}
                        className="w-full"
                        disabled={loading}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>30m</span>
                        <span>180m</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty Level
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {['easy', 'medium', 'hard'].map((level) => (
                        <button
                          key={level}
                          onClick={() => setFormData(prev => ({ ...prev, difficulty: level }))}
                          disabled={loading}
                          className={`px-4 py-3 rounded-lg font-medium transition-colors capitalize ${
                            formData.difficulty === level
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } disabled:opacity-50`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleGenerate}
                    disabled={loading || !formData.examName.trim() || !formData.subjects.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-lg"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Generating Mock Test...
                      </>
                    ) : (
                      <>
                        ✨ Generate Mock Test
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Tips Section */}
            <div className="lg:col-span-1">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 sticky top-4">
                <h3 className="text-lg font-bold text-blue-900 mb-3">
                  💡 Mock Test Features
                </h3>
                <ul className="space-y-3 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Real exam-like patterns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Timed test environment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Comprehensive coverage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Instant results & analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Performance tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Detailed explanations</span>
                  </li>
                </ul>

                <div className="mt-6 p-3 bg-white rounded-lg border border-blue-300">
                  <p className="text-xs text-gray-600">
                    <strong>Tip:</strong> Take mock tests regularly to identify weak areas and improve exam readiness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Test Generated - Ready to Start
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">🎯</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                  Mock Test Ready!
                </h2>
                <p className="text-gray-600">
                  Your comprehensive test has been generated
                </p>
              </div>

              {/* Test Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-purple-50 rounded-lg p-4 text-center border-2 border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">
                    {generatedTest.total_questions}
                  </div>
                  <div className="text-sm text-purple-800 mt-1">Questions</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center border-2 border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {formData.duration}m
                  </div>
                  <div className="text-sm text-blue-800 mt-1">Duration</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center border-2 border-green-200">
                  <div className="text-2xl font-bold text-green-600 capitalize">
                    {formData.difficulty}
                  </div>
                  <div className="text-sm text-green-800 mt-1">Difficulty</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center border-2 border-orange-200">
                  <div className="text-2xl font-bold text-orange-600">
                    {generatedTest.total_questions}
                  </div>
                  <div className="text-sm text-orange-800 mt-1">Total Marks</div>
                </div>
              </div>

              {/* Test Info */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-lg text-gray-800 mb-3">
                  {generatedTest.exam_name || formData.examName}
                </h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p><strong>Subjects:</strong> {formData.subjects}</p>
                  <p><strong>Format:</strong> Multiple Choice Questions (MCQ)</p>
                  <p><strong>Marking:</strong> 1 mark per correct answer</p>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h4 className="font-bold text-yellow-900 mb-2">📌 Instructions:</h4>
                <ul className="space-y-1 text-sm text-yellow-800">
                  <li>• All questions are compulsory</li>
                  <li>• Each question carries 1 mark</li>
                  <li>• Timer will start when you begin the test</li>
                  <li>• You can navigate between questions</li>
                  <li>• Review your answers before submission</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleStartTest}
                  className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-lg"
                >
                  🚀 Start Test Now
                </button>
                <button
                  onClick={handleReset}
                  className="px-8 py-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  Generate New Test
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AIMockTestGenerator;
